'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter, usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { motion, AnimatePresence } from 'framer-motion';
import { Pencil, X } from 'lucide-react';
import { api, User, SiteConfig } from '@/lib/api';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui';


interface NavItem {
    id: string;
    label: string;
    href: string;
    show?: boolean;
    icon?: React.ReactNode;
    badge?: number;
}

const LOGO_SIZE_PX: Record<string, number> = { small: 40, medium: 64, large: 80 };

export function Header() {
    const t = useTranslations('nav');
    const router = useRouter();
    const pathname = usePathname();
    const { user, isAuthenticated, logout } = useAuth();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [pendingUsersCount, setPendingUsersCount] = useState(0);
    const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);
    const [showEditPanel, setShowEditPanel] = useState(false);
    const [uploadingHeader, setUploadingHeader] = useState(false);
    const [uploadingFavicon, setUploadingFavicon] = useState(false);
    const [confirmDeleteLogo, setConfirmDeleteLogo] = useState(false);
    const [confirmDeleteFavicon, setConfirmDeleteFavicon] = useState(false);
    const [headerTextInput, setHeaderTextInput] = useState('');
    const [useHamburger, setUseHamburger] = useState(false);
    const headerLogoInputRef = useRef<HTMLInputElement>(null);
    const faviconInputRef = useRef<HTMLInputElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const navRef = useRef<HTMLElement>(null);
    const rightRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    const [headerHeight, setHeaderHeight] = useState(64);

    // Scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Fetch site config
    useEffect(() => {
        api.getSiteConfig().then(config => {
            setSiteConfig(config);
            setHeaderTextInput(config.header_text || '');
        }).catch(() => {});
    }, []);

    // Close panel on outside click
    useEffect(() => {
        if (!showEditPanel) return;
        const handler = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setShowEditPanel(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showEditPanel]);

    // Fetch pending counts for root users
    useEffect(() => {
        const fetchPendingCounts = async () => {
            if (user?.role === 'root') {
                try {
                    const [applications, usersCount] = await Promise.all([
                        api.getApplications('pending'),
                        api.getPendingUsersCount(),
                    ]);
                    setPendingCount(applications.length);
                    setPendingUsersCount(usersCount.count);
                } catch (err) {
                    console.error('Failed to fetch pending counts');
                }
            }
        };

        fetchPendingCounts();
        const interval = setInterval(fetchPendingCounts, 30000);

        window.addEventListener('pendingUsersChanged', fetchPendingCounts);
        window.addEventListener('pendingApplicationsChanged', fetchPendingCounts);
        return () => {
            clearInterval(interval);
            window.removeEventListener('pendingUsersChanged', fetchPendingCounts);
            window.removeEventListener('pendingApplicationsChanged', fetchPendingCounts);
        };
    }, [user]);

    // Track header height for menu positioning
    useEffect(() => {
        const update = () => {
            if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
        };
        update();
        const ro = new ResizeObserver(update);
        if (headerRef.current) ro.observe(headerRef.current);
        return () => ro.disconnect();
    }, [isScrolled]);

    // Detect nav overflow and switch to hamburger
    useEffect(() => {
        const check = () => {
            if (!containerRef.current || !navRef.current || !rightRef.current || !panelRef.current) return;
            const containerRect = containerRef.current.getBoundingClientRect();
            const logoRect = panelRef.current.getBoundingClientRect();
            const rightRect = rightRef.current.getBoundingClientRect();
            const navWidth = navRef.current.scrollWidth;
            const gap = 24;
            // Nav is absolutely centered — calculate where its edges would land
            const mid = containerRect.left + containerRect.width / 2;
            const navLeft = mid - navWidth / 2;
            const navRight = mid + navWidth / 2;
            setUseHamburger(navRight > rightRect.left - gap || navLeft < logoRect.right + gap);
        };
        const ro = new ResizeObserver(check);
        ro.observe(document.documentElement);
        check();
        return () => ro.disconnect();
    }, []);

    const handleLogout = async () => {
        await logout();
    };

    const handleHeaderLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingHeader(true);
        try {
            const updated = await api.uploadHeaderLogo(file);
            setSiteConfig(updated);
        } finally {
            setUploadingHeader(false);
            if (headerLogoInputRef.current) headerLogoInputRef.current.value = '';
        }
    };

    const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingFavicon(true);
        try {
            const updated = await api.uploadFavicon(file);
            setSiteConfig(updated);
            // Update favicon in DOM immediately
            const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
            if (link && updated.favicon_path) {
                link.href = updated.favicon_path + '?t=' + Date.now();
            }
        } finally {
            setUploadingFavicon(false);
            if (faviconInputRef.current) faviconInputRef.current.value = '';
        }
    };

    const handleDeleteLogo = async () => {
        if (!confirmDeleteLogo) {
            setConfirmDeleteLogo(true);
            setTimeout(() => setConfirmDeleteLogo(false), 3000);
            return;
        }
        const updated = await api.deleteHeaderLogo();
        setSiteConfig(updated);
        setConfirmDeleteLogo(false);
    };

    const handleDeleteFavicon = async () => {
        if (!confirmDeleteFavicon) {
            setConfirmDeleteFavicon(true);
            setTimeout(() => setConfirmDeleteFavicon(false), 3000);
            return;
        }
        const updated = await api.deleteFavicon();
        setSiteConfig(updated);
        setConfirmDeleteFavicon(false);
        const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
        if (link) link.href = '/favicon.ico';
    };

    const handleSizeChange = async (size: string) => {
        const updated = await api.updateSiteConfig({ header_logo_size: size });
        setSiteConfig(updated);
    };

    const handleTextSave = async () => {
        const updated = await api.updateSiteConfig({ header_text: headerTextInput });
        setSiteConfig(updated);
    };

    const isRoot = user?.role === 'root';
    const isAuditor = user?.role === 'auditor';

    const navItems: NavItem[] = [
        {
            id: 'about',
            label: t('about'),
            href: '/about',
            show: true
        },
        {
            id: 'news',
            label: t('news'),
            href: '/news',
            show: true
        },
        {
            id: 'projects',
            label: t('projects'),
            href: '/certifications-showcase',
            show: !isAuthenticated
        },
        // Authenticated items
        {
            id: 'certifications',
            label: t('projects'),
            href: '/certifications',
            show: !!isAuthenticated
        },
        {
            id: 'auditors',
            label: t('auditors'),
            href: '/auditors',
            show: !isAuthenticated || !isRoot
        },
        {
            id: 'apply-auditor',
            label: t('applyAuditor'),
            href: '/apply-auditor',
            show: !!isAuthenticated && !isRoot && !isAuditor
        },
        {
            id: 'users',
            label: t('users'),
            href: '/admin/users',
            show: isRoot,
            badge: pendingUsersCount
        },
        {
            id: 'applications',
            label: t('applications'),
            href: '/admin/applications',
            show: isRoot,
            badge: pendingCount
        },
        {
            id: 'buildingTypes',
            label: t('buildingTypes'),
            href: '/admin/weight-profiles',
            show: isRoot
        },
        {
            id: 'scores',
            label: t('scores'),
            href: '/admin/scores',
            show: isRoot
        },
        {
            id: 'emailTemplates',
            label: t('emailTemplates'),
            href: '/admin/email-templates',
            show: isRoot
        },
    ];

    const toggleLanguage = () => {
        const currentLocale = document.cookie.match(/NEXT_LOCALE=(\w+)/)?.[1] || 'en';
        const newLocale = currentLocale.toLowerCase() === 'pt' ? 'en' : 'pt';
        document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=31536000; SameSite=Lax`;
        router.refresh();
    };

    const logoSrc = siteConfig?.header_logo_path ?? null;
    const logoSizePx = LOGO_SIZE_PX[siteConfig?.header_logo_size || 'medium'] ?? 64;

    return (
        <header
            ref={headerRef}
            className={`
                fixed top-0 left-0 right-0 z-50 print:hidden
                transition-all duration-300
                bg-white/70 backdrop-blur-md border-b border-white/20 shadow-sm
                ${isScrolled ? 'py-1' : 'py-2'}
            `}
        >
            <div ref={containerRef} className="max-w-8xl mx-auto px-6 md:px-10 flex items-center justify-between">
                {/* Logo */}
                <div className="z-50 relative" ref={panelRef}>
                    <div className="relative group">
                        <Link href="/" className="flex items-center gap-3">
                            {logoSrc && (
                                <Image
                                    src={logoSrc}
                                    alt="Site Logo"
                                    width={200}
                                    height={logoSizePx}
                                    className="w-auto object-contain transition-all duration-300"
                                    style={{ height: logoSizePx }}
                                    priority
                                    unoptimized
                                />
                            )}
                            {siteConfig?.header_text && (
                                <span className="text-black font-semibold text-lg leading-tight whitespace-nowrap">
                                    {siteConfig.header_text}
                                </span>
                            )}
                        </Link>
                        {isRoot && (
                            <button
                                onClick={() => setShowEditPanel(v => !v)}
                                className="absolute -top-1 -right-5 p-1 bg-white/80 border border-slate-200 text-slate-500 hover:text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer shadow-sm"
                                title="Edit site images"
                            >
                                <Pencil className="w-3 h-3" />
                            </button>
                        )}
                    </div>

                    {/* Edit panel */}
                    {showEditPanel && (
                        <div className="absolute top-full left-0 mt-2 bg-white border border-slate-200 shadow-xl p-4 z-50 w-72">
                            <button
                                onClick={() => setShowEditPanel(false)}
                                className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-700 cursor-pointer"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>

                            {/* Header Logo */}
                            <div className="mb-4">
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Header Logo</p>
                                <div className="flex items-start gap-3">
                                    <div className="w-24 h-14 bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
                                        {logoSrc
                                            ? <img src={logoSrc} alt="" className="max-w-full max-h-full object-contain" />
                                            : <span className="text-slate-300 text-xs">No image</span>
                                        }
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <div className="flex gap-1.5">
                                            <label className={`px-2 py-1 text-xs border cursor-pointer transition-colors ${uploadingHeader ? 'bg-slate-50 text-slate-400 border-slate-200' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300'}`}>
                                                {uploadingHeader ? 'Uploading…' : 'Upload'}
                                                <input
                                                    ref={headerLogoInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={handleHeaderLogoUpload}
                                                    disabled={uploadingHeader}
                                                />
                                            </label>
                                            {logoSrc && (
                                                <button
                                                    onClick={handleDeleteLogo}
                                                    className={`px-2 py-1 text-xs border cursor-pointer transition-colors ${confirmDeleteLogo ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-rose-500 border-rose-300 hover:bg-rose-50'}`}
                                                >
                                                    {confirmDeleteLogo ? 'Confirm' : 'Delete'}
                                                </button>
                                            )}
                                        </div>
                                        <div className="flex gap-1">
                                            {(['small', 'medium', 'large'] as const).map(s => (
                                                <button
                                                    key={s}
                                                    onClick={() => handleSizeChange(s)}
                                                    className={`px-2 py-0.5 text-xs border cursor-pointer transition-colors ${(siteConfig?.header_logo_size || 'medium') === s ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                                                >
                                                    {s.charAt(0).toUpperCase()}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-slate-200 my-3" />

                            {/* Header Text */}
                            <div className="mb-4">
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Header Text</p>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={headerTextInput}
                                        onChange={e => setHeaderTextInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleTextSave()}
                                        placeholder="Site name or tagline…"
                                        className="flex-1 px-2 py-1 text-xs border border-slate-300 outline-none focus:border-slate-600 min-w-0"
                                    />
                                    <button
                                        onClick={handleTextSave}
                                        className="px-2 py-1 text-xs bg-slate-900 text-white border border-slate-900 hover:bg-slate-700 cursor-pointer transition-colors shrink-0"
                                    >
                                        Save
                                    </button>
                                </div>
                                <p className="text-xs text-slate-400 mt-1">Shown alongside or instead of the logo image.</p>
                            </div>

                            <div className="border-t border-slate-200 my-3" />

                            {/* Favicon */}
                            <div>
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Browser Tab Icon</p>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
                                        {siteConfig?.favicon_path ? (
                                            <img src={siteConfig.favicon_path} alt="" className="max-w-full max-h-full object-contain" />
                                        ) : (
                                            <span className="text-slate-300 text-xs">–</span>
                                        )}
                                    </div>
                                    <div className="flex gap-1.5">
                                        <label className={`px-2 py-1 text-xs border cursor-pointer transition-colors ${uploadingFavicon ? 'bg-slate-50 text-slate-400 border-slate-200' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300'}`}>
                                            {uploadingFavicon ? 'Uploading…' : 'Upload'}
                                            <input
                                                ref={faviconInputRef}
                                                type="file"
                                                accept="image/*,.ico"
                                                className="hidden"
                                                onChange={handleFaviconUpload}
                                                disabled={uploadingFavicon}
                                            />
                                        </label>
                                        {siteConfig?.favicon_path && (
                                            <button
                                                onClick={handleDeleteFavicon}
                                                className={`px-2 py-1 text-xs border cursor-pointer transition-colors ${confirmDeleteFavicon ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-rose-500 border-rose-300 hover:bg-rose-50'}`}
                                            >
                                                {confirmDeleteFavicon ? 'Confirm' : 'Delete'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Desktop Nav */}
                <nav ref={navRef} className={`${useHamburger ? 'invisible pointer-events-none flex' : 'hidden md:flex'} items-center gap-8 absolute left-1/2 -translate-x-1/2`}>
                    {navItems.filter(item => item.show !== false).map((item) => {
                        const isActive = pathname === item.href;
                        return (
                            <Link
                                key={item.id}
                                href={item.href}
                                className={`
                                    relative text-sm font-medium transition-colors duration-200
                                    ${isActive ? 'text-black' : 'text-gray-500 hover:text-black'}
                                `}
                            >
                                {item.label}
                                {item.badge !== undefined && item.badge > 0 && (
                                    <span className="absolute -top-2 -right-3 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                        {item.badge}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Right Side */}
                <div ref={rightRef} className="flex items-center gap-6 z-50 relative">
                    {/* Language Switch */}
                    <button
                        onClick={toggleLanguage}
                        className="text-xs font-medium text-gray-500 hover:text-black transition-colors uppercase tracking-wider cursor-pointer"
                    >
                        PT / EN
                    </button>

                    {isAuthenticated ? (
                        <div className="flex items-center gap-4">

                            <div className="hidden md:flex items-center gap-3">
                                <Link href="/profile" title="Click to edit profile">
                                    {user?.profile_picture_path ? (
                                        <img
                                            src={api.getProfilePictureUrl(user?.id)}
                                            alt={`${user?.first_name} ${user?.last_name}`}
                                            className="w-8 h-8 object-cover rounded border border-gray-200 hover:border-gray-400 transition-colors"
                                        />
                                    ) : (
                                        <div className="w-8 h-8 bg-gray-200 rounded border border-gray-300 flex items-center justify-center text-xs font-semibold text-gray-600 hover:bg-gray-300 transition-colors">
                                            {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                                        </div>
                                    )}
                                </Link>
                                <Link href="/profile" className="text-sm font-medium text-black hover:text-gray-600 transition-colors">
                                    {user?.first_name} {user?.last_name}
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="text-gray-500 hover:text-black transition-colors p-1 cursor-pointer"
                                    title="Logout"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="hidden md:flex items-center gap-4">
                            <Link href="/login" className="text-sm font-medium text-black hover:text-gray-600 transition-colors">
                                Login
                            </Link>
                            <Link href="/signup" className="text-sm font-medium text-black hover:text-gray-600 transition-colors">
                                Sign Up
                            </Link>
                        </div>
                    )}

                    {/* Mobile Menu Toggle */}
                    <button
                        className={`${useHamburger ? 'block' : 'md:hidden'} text-black cursor-pointer`}
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                    >
                        {isMenuOpen ? (
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className={`fixed left-0 right-0 bg-white/95 backdrop-blur-md z-40 pb-6 px-6 border-b border-slate-200 shadow-lg ${useHamburger ? '' : 'md:hidden'}`}
                    style={{ top: headerHeight }}
                    >
                        <nav className="flex flex-col gap-6">
                            {navItems.filter(item => item.show !== false).map((item) => (
                                <Link
                                    key={item.id}
                                    href={item.href}
                                    className="text-2xl font-medium text-black relative inline-block w-fit"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    {item.label}
                                    {item.badge !== undefined && item.badge > 0 && (
                                        <span className="absolute -top-2 -right-8 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                            {item.badge}
                                        </span>
                                    )}
                                </Link>
                            ))}
                            {!isAuthenticated && (
                                <div className="flex flex-col gap-4 mt-8">
                                    <Link href="/login" onClick={() => setIsMenuOpen(false)} className="text-xl text-black">
                                        Login
                                    </Link>
                                    <Link href="/signup" onClick={() => setIsMenuOpen(false)} className="text-xl text-gray-500">
                                        Sign Up
                                    </Link>
                                </div>
                            )}
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}

export default Header;
